/**
 * Partial date precision model.
 *
 * Tiered date precision (`full | month | year`) for fields where the user may
 * not know the exact day or month — typically date of birth and date of death
 * for distant relatives.
 *
 * Wire format (backend):
 *   - `dob` / `dod` stay as full ISO `YYYY-MM-DD` strings, with missing
 *     components filled by `01` (year-only → `"1980-01-01"`,
 *     month → `"1980-06-01"`).  This preserves byte compatibility with the
 *     XML export pipeline (`generateUserXML` → `parseDate`) consumed by the
 *     desktop importer.
 *   - `estimatedDob` / `estimatedDod` booleans remain truthy whenever
 *     precision is anything other than `full`.
 *   - `dobPrecision` / `dodPrecision` are the new source of truth — string
 *     enum `'full' | 'month' | 'year'`.  Older records with this field
 *     undefined fall back to the legacy boolean.
 *
 * Frontend representation:
 *   `PartialDate` carries year/month/day plus precision so display sites can
 *   render `46`, `~46`, or `45–46` instead of falsely-precise integers.
 *
 * @typedef {'full' | 'month' | 'year'} DatePrecision
 *
 * @typedef {Object} PartialDate
 * @property {number}      year        4-digit year.
 * @property {number|null} month       1–12 when precision is `full` or `month`, else null.
 * @property {number|null} day         1–31 when precision is `full`, else null.
 * @property {DatePrecision} precision Least-precise component the user knows.
 */

export const PRECISION = Object.freeze({
  FULL: 'full',
  MONTH: 'month',
  YEAR: 'year'
});

// A string carrying an explicit UTC offset ("…Z", "…+09:30") denotes a real
// instant, so its calendar day must be read in UTC. Anything else — a bare
// "YYYY-MM-DD", a Date object, a local date-time with no offset — denotes a
// calendar date someone picked, and must be read in local time.
const HAS_EXPLICIT_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parse a date into year/month/day numbers.
 * Used internally — does not infer precision.
 *
 * TGCLOUD-1631: this used to read the fallback branch with getUTC*, which is
 * only correct for a genuine instant. A `Date` built at local midnight — which
 * is exactly what the MUI date pickers produce — came out a day early at any
 * positive UTC offset, so a twin's copied date of birth showed 14 May for a
 * proband born on the 15th. In the UK the offset in force is the one on the
 * date of birth, so summer birthdays were wrong and winter ones right.
 *
 * @param {string|Date|null|undefined} iso
 * @returns {{ year: number, month: number|null, day: number|null }|null}
 */
export const parsePartialDateString = (iso) => {
  if (!iso) return null;

  // A Date instance is a calendar date the user picked, held at local
  // midnight. Read its local components — never its UTC ones.
  if (iso instanceof Date) {
    if (Number.isNaN(iso.getTime())) return null;
    return {
      year: iso.getFullYear(),
      month: iso.getMonth() + 1,
      day: iso.getDate()
    };
  }

  const str = String(iso).trim();
  if (!str) return null;
  // Accept "YYYY", "YYYY-MM", "YYYY-MM-DD"
  const match = /^(\d{4})(?:-(\d{1,2}))?(?:-(\d{1,2}))?$/.exec(str);
  if (!match) {
    // Fallback: try Date parse (handles ISO with time component)
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return null;
    const utc = HAS_EXPLICIT_OFFSET.test(str);
    return {
      year: utc ? d.getUTCFullYear() : d.getFullYear(),
      month: (utc ? d.getUTCMonth() : d.getMonth()) + 1,
      day: utc ? d.getUTCDate() : d.getDate()
    };
  }
  const year = parseInt(match[1], 10);
  const month = match[2] ? parseInt(match[2], 10) : null;
  const day = match[3] ? parseInt(match[3], 10) : null;
  return { year, month, day };
};

/**
 * Convert a stored date into a `Date` positioned at LOCAL midnight.
 *
 * `new Date('1976-05-15')` is specified to parse as UTC midnight, so feeding a
 * date-only string straight to a date picker renders the previous day for
 * anyone at a negative UTC offset — the mirror of TGCLOUD-1631. Everything the
 * questionnaire stores in `dob`/`dod` is a calendar date, not an instant, so
 * it must be rebuilt component-wise in local time.
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
/**
 * Build a `Date` at LOCAL midnight from calendar parts.
 *
 * `new Date(year, month, day)` remaps years 0-99 onto 1900+year, so year 1
 * silently becomes 1901 and year 19 becomes 1919. That is not a theoretical
 * edge case: MUI's picker publishes a value on every keystroke, so someone
 * typing "1982" passes through year 1, then 19, then 198 — and each of those
 * was being rewritten into a plausible-looking 20th-century year on its way
 * back into the field. `setFullYear` is the only way to defeat the remap.
 *
 * There must be exactly ONE of these. Two independent copies of this
 * construction already existed — here and in PartialDateInput — which is why
 * fixing either one on its own fixed nothing (TGCLOUD-1650).
 *
 * @param {number} year
 * @param {number} [month] 1-12
 * @param {number} [day]
 * @returns {Date|null}
 */
export const localDateFromParts = (year, month = 1, day = 1) => {
  const d = new Date(year, (month ?? 1) - 1, day ?? 1);
  if (Number.isNaN(d.getTime())) return null;
  if (year >= 0 && year < 100) d.setFullYear(year);
  return d;
};

export const toLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  const parts = parsePartialDateString(value);
  if (!parts) return null;
  return localDateFromParts(parts.year, parts.month, parts.day);
};

/**
 * Read the calendar day of a value from an INSTANT column — a Mongoose
 * `type: Date` field, as opposed to the `type: String` 'YYYY-MM-DD' columns
 * `toLocalDate` was written for.
 *
 * The distinction is not cosmetic, and it is the gap that produced TGCLOUD-1707.
 * `toLocalDate` reads an offset-bearing string ("…Z") by its **UTC** components,
 * which is right for a date-only value that was WRITTEN UTC-anchored — the common
 * case here, because `toStoredDate` writes 'YYYY-MM-DD' and Mongoose casts that
 * to UTC midnight. But a `type: Date` column fed straight from a DatePicker holds
 * the LOCAL midnight of the day the user clicked, so it serialises to an instant
 * that is NOT UTC midnight:
 *
 *   picked 12 Mar in Sydney (+11)  ->  "2026-03-11T13:00:00.000Z"
 *     toLocalDate            -> 11 Mar   ✗ (UTC components)
 *     toLocalDateFromInstant -> 12 Mar   ✓ (local components)
 *
 * Reading such a value with `toLocalDate` moves it back a day at every POSITIVE
 * offset — the mirror of the UTC-midnight bug, and invisible at UTC exactly like
 * its twin.
 *
 * A value with no explicit offset (a bare 'YYYY-MM-DD', a Date object, a
 * local date-time) is delegated to `toLocalDate`, so this is a safe superset:
 * calendar dates keep calendar semantics and instants get instant semantics.
 *
 * WHICH ONE A FIELD NEEDS IS DECIDED BY HOW IT IS WRITTEN, not by its declared
 * Mongoose type — the type alone is not enough to tell, and guessing from it is
 * how you swap one offset's bug for the other's:
 *
 *   written as a raw picker `Date`        -> local-anchored instant -> THIS
 *     e.g. pregnancy `dateOfRecord`, contact-note `assignedDate`
 *   written via `toStoredDate` (a string) -> UTC-anchored midnight  -> toLocalDate
 *     e.g. workflow `startDate` — a `type: Date` column all the same, because
 *     Mongoose casts the 'YYYY-MM-DD' it is given to UTC midnight
 *   a `type: String` 'YYYY-MM-DD' column  -> calendar date          -> toLocalDate
 *
 * Reading a UTC-anchored value with THIS function shifts it back a day at every
 * NEGATIVE offset, which is the original bug over again. Check the writer.
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export const toLocalDateFromInstant = (value) => {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string' || !HAS_EXPLICIT_OFFSET.test(value.trim()))
    return toLocalDate(value);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Build a `PartialDate` from the backend record's date triple.
 *
 *   { iso: "1980-06-15", estimatedFlag: false, precisionField: undefined }
 *     → { year: 1980, month: 6, day: 15, precision: 'full' }
 *
 *   { iso: "1980-01-01", estimatedFlag: true, precisionField: undefined }
 *     → { year: 1980, month: null, day: null, precision: 'year' }   // legacy
 *
 *   { iso: "1980-06-01", estimatedFlag: true, precisionField: 'month' }
 *     → { year: 1980, month: 6, day: null, precision: 'month' }
 *
 * Trusts `precisionField` when present; otherwise falls back to the legacy
 * boolean (true → year precision; false → full precision).
 *
 * @param {Object} args
 * @param {string|null|undefined} args.iso
 * @param {boolean|undefined}     args.estimatedFlag
 * @param {DatePrecision|undefined} args.precisionField
 * @returns {PartialDate|null}
 */
export const partialDateFromBackend = ({
  iso,
  estimatedFlag,
  precisionField
} = {}) => {
  const parts = parsePartialDateString(iso);
  if (!parts) return null;

  const precision =
    precisionField === PRECISION.YEAR ||
    precisionField === PRECISION.MONTH ||
    precisionField === PRECISION.FULL
      ? precisionField
      : estimatedFlag
      ? PRECISION.YEAR
      : PRECISION.FULL;

  if (precision === PRECISION.YEAR) {
    return { year: parts.year, month: null, day: null, precision };
  }
  if (precision === PRECISION.MONTH) {
    return {
      year: parts.year,
      month: parts.month ?? null,
      day: null,
      precision
    };
  }
  return {
    year: parts.year,
    month: parts.month ?? null,
    day: parts.day ?? null,
    precision: PRECISION.FULL
  };
};

/**
 * Serialize a `PartialDate` to the backend's three-field representation.
 *
 *   partialDateToBackend({ year:1980, month:null, day:null, precision:'year' })
 *     → { dob: "1980-01-01", estimatedDob: true, dobPrecision: 'year' }
 *
 *   partialDateToBackend({ year:1980, month:6, day:15, precision:'full' })
 *     → { dob: "1980-06-15", estimatedDob: false, dobPrecision: 'full' }
 *
 * Uses `01` padding for missing month/day so the legacy `dob`/`dod` String
 * field stays a full ISO date — required for XML-export compatibility.
 *
 * @param {PartialDate|null} partial
 * @param {{ date: string, estimated: string, precision: string }} fieldNames
 *   e.g. `{ date: 'dob', estimated: 'estimatedDob', precision: 'dobPrecision' }`
 * @returns {Object} A patch object with the three fields. Date is null when
 *   `partial` is null/undefined; estimated and precision still carry sane
 *   defaults so the record clears cleanly.
 */
export const partialDateToBackend = (partial, fieldNames) => {
  const { date, estimated, precision } = fieldNames;
  if (!partial) {
    // Empty string (not null) — `dob`/`dod` are QE-encrypted strings on the
    // backend and CSFLE refuses to encrypt null (Location31041).
    return {
      [date]: '',
      [estimated]: false,
      [precision]: PRECISION.FULL
    };
  }
  const yearStr = String(partial.year).padStart(4, '0');
  const monthStr = String(partial.month ?? 1).padStart(2, '0');
  const dayStr = String(partial.day ?? 1).padStart(2, '0');
  return {
    [date]: `${yearStr}-${monthStr}-${dayStr}`,
    [estimated]: partial.precision !== PRECISION.FULL,
    [precision]: partial.precision
  };
};

/**
 * Format a `PartialDate` for human display.
 *   full  → "15/06/1980"
 *   month → "06/1980"
 *   year  → "1980"
 *
 * @param {PartialDate|null|undefined} partial
 * @returns {string}
 */
export const formatPartialDate = (partial) => {
  if (!partial) return '';
  const yearStr = String(partial.year);
  if (partial.precision === PRECISION.YEAR) {
    return yearStr;
  }
  const monthStr = String(partial.month ?? 1).padStart(2, '0');
  if (partial.precision === PRECISION.MONTH) {
    return `${monthStr}/${yearStr}`;
  }
  const dayStr = String(partial.day ?? 1).padStart(2, '0');
  return `${dayStr}/${monthStr}/${yearStr}`;
};
